import { useEffect, useState } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n/context';
import ListToolbar from '../ui/ListToolbar';
import '../styles/ui.css';
import './ListPages.css';

const POLL_INTERVAL_MS = 5000;

export default function SystemStatus() {
  const { t, formatDateTime } = useI18n();
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [severity, setSeverity] = useState('');

  function fetchStatus() {
    const params = new URLSearchParams({ page: 1, limit: 50 });
    if (source) params.set('source', source);
    if (severity) params.set('severity', severity);
    api(`/superadmin/system-status?${params}`)
      .then((res) => {
        setHealth(res.health || null);
        setErrors(res.recentErrors || []);
        setPagination(res.pagination || { page: 1, limit: 50, total: 0 });
      })
      .catch(() => {
        setHealth(null);
        setErrors([]);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, severity]);

  function formatUptime(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('systemStatus.title')}</h1>
        <p className="sa-page-subtitle">{t('systemStatus.subtitle')}</p>
      </div>

      <div className="sa-card" style={{ padding: '0.75rem 24px', margin: '12px 0 0', display: 'flex', gap: '2rem', flexShrink: 0 }}>
        <div>
          <strong>{t('systemStatus.healthServer')}: </strong>
          <span className={`sa-badge ${health?.server === 'ok' ? 'sa-badge--success' : 'sa-badge--error'}`}>
            {health?.server === 'ok' ? t('systemStatus.statusOk') : t('systemStatus.statusError')}
          </span>
        </div>
        <div>
          <strong>{t('systemStatus.healthDb')}: </strong>
          <span className={`sa-badge ${health?.db === 'ok' ? 'sa-badge--success' : 'sa-badge--error'}`}>
            {health?.db === 'ok' ? t('systemStatus.statusOk') : t('systemStatus.statusError')}
          </span>
        </div>
        <div>
          <strong>{t('systemStatus.healthUptime')}: </strong>
          {formatUptime(health?.uptimeSeconds)}
        </div>
      </div>

      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          filters={[
            <select
              key="source"
              className="ui-input"
              aria-label={t('systemStatus.sourceFilterLabel')}
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="">{t('systemStatus.sourceFilterLabel')}: {t('systemStatus.filterAll')}</option>
              <option value="backend">{t('systemStatus.sourceBackend')}</option>
              <option value="client">{t('systemStatus.sourceClient')}</option>
            </select>,
            <select
              key="severity"
              className="ui-input"
              aria-label={t('systemStatus.severityFilterLabel')}
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              <option value="">{t('systemStatus.severityFilterLabel')}: {t('systemStatus.filterAll')}</option>
              <option value="error">{t('systemStatus.severityError')}</option>
              <option value="warning">{t('systemStatus.severityWarning')}</option>
            </select>
          ]}
          actions={null}
        />
      </div>

      <div className="ui-page-content">
        <div className="sa-card sa-audit-list-card">
          {loading ? (
            <div className="sa-loading">{t('common.loading')}</div>
          ) : errors.length === 0 ? (
            <div className="sa-empty">{t('systemStatus.empty')}</div>
          ) : (
            <>
              <div className="sa-audit-list">
                {errors.map((e) => (
                  <div key={e.id} className="sa-audit-item" style={{ display: 'block' }}>
                    <div className="sa-audit-header">
                      <div>
                        <div className="sa-audit-action">
                          <span className={`sa-badge ${e.source === 'backend' ? 'sa-badge--error' : 'sa-badge--success'}`}>
                            {e.source === 'backend' ? t('systemStatus.backendBadge') : t('systemStatus.clientBadge')}
                          </span>{' '}
                          {e.message}
                        </div>
                        <div className="sa-audit-actor">
                          {e.timestamp ? formatDateTime(e.timestamp) : '—'}
                          {e.userId ? ` — ${t('systemStatus.userLabel')} ${e.userId}` : ''}
                        </div>
                      </div>
                    </div>
                    {e.stack && (
                      <div className="sa-audit-detail-panel">
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{e.stack}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="sa-pagination">
                <span className="sa-pagination-info">
                  {pagination.total} {t('common.results')}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
